'use strict';

import Vue from 'vue';
import { forEach } from 'lodash/collection';
import { getParamString } from 'leaflet/src/core/Util';
import { FEATURE_CARD_STATE } from '../geometries/constants';
import store from '../../store';
import router from '../../router';
import { FEATURE_GROUPS, PANES, PANES_OPTIONS } from './constants';
import { FloatingCardsEventBus } from '../mapview/events';
import MarkerCardWrapper from '../../components/MapView/Geometries/Marker/MarkerCardWrapper';
import PolylineCardWrapper from '../../components/MapView/Geometries/Polyline/PolylineCardWrapper';
import FieldCardWrapper from '../../components/MapView/Geometries/Field/FieldCardWrapper';
import VIcon from 'vuetify/lib/components/VIcon/VIcon';
import { Field } from '../fields/models';
import { MainVue } from '../../main';
import { ProcessingGeometry } from '../geometries/models';
import {
	GET_IS_POST_PROCESSING_REQUEST_STATE,
	GET_IS_POST_PROCESSING_RESULTS_STATE,
} from '../../store/modules/mapview';
import {
	PostProcessingsRequestEventBus,
	PostProcessingsResultsEventBus,
} from '../post-processings/events';
import { LAYER_COLORS } from '../mapview/constants';
import EvaluateCardWrapper from '@/components/MapView/Geometries/Evaluate/EvaluateCardWrapper';

function create$1(tagName, className, container) {
	let el = document.createElement(tagName);
	el.className = className || '';

	if (container) {
		container.appendChild(el);
	}
	return el;
}

(function(factory, window) {
	// define an AMD module that relies on 'leaflet'
	// eslint-disable-next-line no-undef
	if (typeof define === 'function' && define.amd) {
		// eslint-disable-next-line no-undef
		define(['leaflet'], factory);

		// define a Common JS module that relies on 'leaflet'
	} else if (typeof exports === 'object') {
		module.exports = factory(require('leaflet'));
	}

	// attach your plugin to the global 'L' variable
	if (typeof window !== 'undefined' && window.L) {
		// eslint-disable-next-line no-undef
		window.L.HorusLeaflet = factory(L);
	}
})(function(L) {
	const Wkt = require('wicket');
	const wkt = new Wkt.Wkt();

	const HorusLeaflet = {};
	HorusLeaflet.Map = L.Map.extend({
		initialize: function(id, options) {
			L.Map.prototype.initialize.call(this, id, options);

			this.panes = {};
			forEach(
				PANES,
				function(pane) {
					this.panes[pane] = this.createPane(pane);
					this.panes[pane].style.zIndex =
						PANES_OPTIONS[pane].style.zIndex;
				}.bind(this),
			);

			this.featureGroups = {};
			forEach(
				FEATURE_GROUPS,
				function(featureGroup) {
					this.featureGroups[featureGroup] = new L.featureGroup({
						pane: PANES[featureGroup],
					});
					this.addLayer(this.featureGroups[featureGroup]);
				}.bind(this),
			);

			this.sideBySide = {
				leftLayers: [],
				rightLayers: [],
				idleLayers: [],
				control: null,
			};

			// SideBySide
			this.sideBySide.control = L.control.sideBySide(
				this.sideBySide.leftLayers,
				this.sideBySide.rightLayers,
			);

			this.coordinatesControl = L.control
				.coordinates({
					position: 'bottomright', //optional default "bottomright"
					decimals: 7, //optional default 4
					decimalSeperator: '.', //optional default "."
					labelTemplateLat: 'Latitude: {y}', //optional default "Lat: {y}"
					labelTemplateLng: 'Longitude: {x}', //optional default "Lng: {x}"
					enableUserInput: false, //optional default true
					useDMS: false, //optional default false
					useLatLngOrder: true, //ordering of labels, default false-> lng-lat
					markerType: L.marker, //optional default L.marker
					markerProps: {}, //optional default {},
				})
				.addTo(this);

			const EvaluateCardClass = Vue.extend(EvaluateCardWrapper);
			const evaluateCardInstance = new EvaluateCardClass({
				parent: MainVue,
				propsData: {
					layer: this,
				},
			});
			evaluateCardInstance.$mount();
			FloatingCardsEventBus.$emit('openEvaluate', evaluateCardInstance);
		},

		removeCoordinatesControl() {
			this.coordinatesControl.remove();
		},

		addCoordinatesControl() {
			this.coordinatesControl.addTo(this);
		},
	});
	HorusLeaflet.map = function(id, options) {
		return new HorusLeaflet.Map(id, options);
	};

	L.Map.include({
		_initControlPos: function() {
			let corners = (this._controlCorners = {}),
				l = 'leaflet-',
				container = (this._controlContainer = create$1(
					'div',
					l + 'control-container',
					this._container,
				));

			function createCorner(vSide, hSide) {
				let className = l + vSide + ' ' + l + hSide;

				corners[vSide + hSide] = create$1('div', className, container);
			}

			createCorner('top', 'left');
			createCorner('top', 'right');
			createCorner('bottom', 'left');
			createCorner('bottom', 'right');
			createCorner('bottom', 'center');
		},
	});

	HorusLeaflet.MarkerIcon = L.Icon.extend({
		createIcon: function() {
			let div = document.createElement('div');

			div.className = 'horus-marker-icon';

			div.innerHTML = this._createInnerIcon();

			this._setIconStyles(div);
			return div;
		},

		_createInnerIcon: function() {
			const VIconClass = Vue.extend({
				components: { VIcon },
				functional: true,
				render(h) {
					return h(
						VIcon,
						{
							props: {
								color: 'info',
							},
						},
						'mdi-map-marker',
					);
				},
			});
			const vIconInstance = new VIconClass({});
			vIconInstance.$mount();
			return vIconInstance.$el.outerHTML;
		},

		_setIconStyles: function(img) {
			let options = this.options,
				size = L.point(options['iconSize']),
				anchor;

			anchor = L.point(options.iconAnchor);

			if (!anchor && size) {
				anchor = size.divideBy(2, true);
			}

			if (anchor) {
				img.style.marginLeft = -anchor.x + 'px';
				img.style.marginTop = -anchor.y + 'px';
			}

			if (size) {
				img.style.width = size.x + 'px';
				img.style.height = size.y + 'px';
			}
		},
	});
	HorusLeaflet.markerIcon = function(options) {
		return new HorusLeaflet.MarkerIcon(options);
	};

	HorusLeaflet.Marker = L.Marker.extend({
		_originalInitialize: L.Marker.prototype.initialize,

		initialize: function(feature, options, map, isCreating = false) {
			const markerIcon = HorusLeaflet.markerIcon({
					iconSize: [36, 36],
					iconAnchor: [18, 36],
				}),
				markerOptions = {
					icon: markerIcon,
					pane: PANES.FEATURES,
					...options,
				},
				latlng = this.parseLatLng(feature['geo_feature']);

			this._originalInitialize(latlng, markerOptions);

			let mode = FEATURE_CARD_STATE.VIEW;
			if (isCreating) mode = FEATURE_CARD_STATE.EDIT;

			const processingGeometry = new ProcessingGeometry(feature);

			const MarkerCardClass = Vue.extend(MarkerCardWrapper);
			const markerCardInstance = new MarkerCardClass({
				parent: MainVue,
				data: () => ({
					mode: mode,
				}),
				propsData: {
					feature: processingGeometry,
					map: map,
					layer: this,
				},
				store,
				router,
			});
			markerCardInstance.$mount();

			if (isCreating) {
				FloatingCardsEventBus.$emit('open', markerCardInstance);
			}

			this.on('click', function() {
				FloatingCardsEventBus.$emit('open', markerCardInstance);
			});
		},

		setViewStyle() {},

		clearStyle() {},

		parseLatLng(geometry) {
			let latlng;
			if (geometry instanceof Object) {
				latlng = geometry['coordinates'];
			} else {
				latlng = wkt.read(geometry).toJson()['coordinates'];
			}
			return latlng.reverse();
		},
	});
	HorusLeaflet.marker = function(feature, options, map, isCreating = false) {
		return new HorusLeaflet.Marker(feature, options, map, isCreating);
	};

	HorusLeaflet.Polyline = L.Polyline.extend({
		_originalInitialize: L.Polyline.prototype.initialize,

		initialize: function(feature, options, map, isCreating = false) {
			const latlngs = this.parseLatLngs(feature['geo_feature']);
			options['fillColor'] = LAYER_COLORS.DEFAULT;
			options['color'] = LAYER_COLORS.DEFAULT;
			this._originalInitialize(latlngs, options);
			this._latlng = this.getBounds().getCenter();

			let mode = FEATURE_CARD_STATE.VIEW;
			if (isCreating) mode = FEATURE_CARD_STATE.EDIT;

			const processingGeometry = new ProcessingGeometry(feature);

			const PolylineCardClass = Vue.extend(PolylineCardWrapper);
			const polylineCardInstance = new PolylineCardClass({
				parent: MainVue,
				data: () => ({
					mode: mode,
				}),
				propsData: {
					feature: processingGeometry,
					map: map,
					layer: this,
				},
			});
			polylineCardInstance.$mount();

			if (isCreating)
				FloatingCardsEventBus.$emit('open', polylineCardInstance);

			this.on('click', function() {
				FloatingCardsEventBus.$emit('open', polylineCardInstance);
			});
		},

		getLatLng: function() {
			return this._latlng;
		},

		// dummy method.
		setLatLng: function() {},

		setViewStyle() {
			this.setStyle({
				fillColor: LAYER_COLORS.VIEW,
				color: LAYER_COLORS.VIEW,
			});
		},

		clearStyle() {
			this.setStyle({
				fillColor: LAYER_COLORS.DEFAULT,
				color: LAYER_COLORS.DEFAULT,
			});
		},

		parseLatLngs(geometry) {
			let latlngs;
			if (geometry instanceof Object) {
				latlngs = geometry['coordinates'];
			} else {
				latlngs = wkt.read(geometry).toJson()['coordinates'];
			}
			return latlngs.map((obj) => obj.reverse());
		},
	});
	HorusLeaflet.polyline = function(
		feature,
		options,
		map,
		isCreating = false,
	) {
		return new HorusLeaflet.Polyline(feature, options, map, isCreating);
	};

	HorusLeaflet.Field = L.Polygon.extend({
		_originalInitialize: L.Polygon.prototype.initialize,

		initialize: function(feature, options, map, isCreating = false) {
			let geoJSON;
			if (feature['geo_field'] instanceof Object)
				geoJSON = feature['geo_field'];
			else geoJSON = wkt.read(feature['geo_field']).toJson();
			const geoJSONLayer = L.GeoJSON.geometryToLayer(geoJSON, options);
			options['fillColor'] = LAYER_COLORS.DEFAULT;
			options['color'] = LAYER_COLORS.DEFAULT;
			this._originalInitialize(geoJSONLayer.getLatLngs(), options);

			this._latlng = this.getBounds().getCenter();

			const isPostProcessingRequestState =
				store.getters[GET_IS_POST_PROCESSING_REQUEST_STATE];
			const isPostProcessingResultsState =
				store.getters[GET_IS_POST_PROCESSING_RESULTS_STATE];

			let mode = FEATURE_CARD_STATE.VIEW;
			if (
				isCreating &&
				!isPostProcessingRequestState &&
				!isPostProcessingResultsState
			)
				mode = FEATURE_CARD_STATE.EDIT;

			const field = new Field(feature);
			const FieldCardClass = Vue.extend(FieldCardWrapper);
			const fieldCardInstance = new FieldCardClass({
				parent: MainVue,
				data: () => ({
					mode: mode,
				}),
				propsData: {
					map: map,
					feature: field,
					layer: this,
				},
			});
			fieldCardInstance.$mount();

			if (
				isCreating &&
				!isPostProcessingRequestState &&
				!isPostProcessingResultsState
			)
				FloatingCardsEventBus.$emit('open', fieldCardInstance);

			this.on('click', function() {
				const isPostProcessingRequestState =
					store.getters[GET_IS_POST_PROCESSING_REQUEST_STATE];
				const isPostProcessingResultsState =
					store.getters[GET_IS_POST_PROCESSING_RESULTS_STATE];
				if (isPostProcessingRequestState)
					PostProcessingsRequestEventBus.$emit('add', {
						field: field,
						layer: this,
					});
				else if (isPostProcessingResultsState)
					PostProcessingsResultsEventBus.$emit('add', {
						field: field,
						layer: this,
					});
				else FloatingCardsEventBus.$emit('open', fieldCardInstance);
			});
		},

		getLatLng: function() {
			return this._latlng;
		},

		// dummy method.
		setLatLng: function() {},

		setPostProcessingRequestStyle() {
			this.setStyle({
				fillColor: LAYER_COLORS.POST_PROCESSING_REQUEST,
				color: LAYER_COLORS.POST_PROCESSING_REQUEST,
			});
		},

		setPostProcessingRequestOverStyle() {
			this.setStyle({
				fillColor: LAYER_COLORS.POST_PROCESSING_REQUEST_OVER,
				color: LAYER_COLORS.POST_PROCESSING_REQUEST_OVER,
			});
		},

		setPostProcessingResultsStyle() {
			this.setStyle({
				fillColor: LAYER_COLORS.POST_PROCESSING_RESULTS,
				color: LAYER_COLORS.POST_PROCESSING_RESULTS,
			});
		},

		setViewStyle() {
			this.setStyle({
				fillColor: LAYER_COLORS.VIEW,
				color: LAYER_COLORS.VIEW,
			});
		},

		clearStyle() {
			this.setStyle({
				fillColor: LAYER_COLORS.DEFAULT,
				color: LAYER_COLORS.DEFAULT,
			});
		},
	});
	HorusLeaflet.field = function(feature, options, map, isCreating = false) {
		return new HorusLeaflet.Field(feature, options, map, isCreating);
	};

	HorusLeaflet.GeoJSON = L.GeoJSON.extend({
		_originalInitialize: L.GeoJSON.prototype.initialize,

		initialize: function(bounds, options) {
			this._originalInitialize(bounds, options);
			this._latlng = this.getBounds().getCenter(); // Define the polygon "center".
		},

		getLatLng: function() {
			return this._latlng;
		},

		// dummy method.
		setLatLng: function() {},
	});
	HorusLeaflet.geoJSON = function(geoJson, options) {
		return new HorusLeaflet.GeoJSON(geoJson, options);
	};

	HorusLeaflet.WMTSLayer = L.TileLayer.extend({
		defaultWmtsParams: {
			service: 'WMTS',
			request: 'GetTile',
			version: '1.0.0',
			layer: '',
			style: '',
			tilematrixSet: '',
			format: 'image/jpeg',
		},
		options: {
			// @option crs: CRS = null
			// Coordinate Reference System to use for the WMS requests, defaults to
			// map CRS. Don't change this if you're not sure what it means.
			crs: L.CRS.EPSG3857,

			// @option uppercase: Boolean = false
			// If `true`, WMTS request parameter keys will be uppercase.
			uppercase: false,
		},
		initialize: function(url, options) {
			// (String, Object)
			this._url = url;
			let wmtsParams = L.extend({}, this.defaultWmtsParams);

			const realRetina = options.detectRetina && L.Browser.retina ? 2 : 1;
			const tileSize = this.getTileSize();
			wmtsParams.width = tileSize.x * realRetina;
			wmtsParams.height = tileSize.y * realRetina;

			for (let i in options) {
				// all keys that are not TileLayer options go to WMTS params
				if (!this.options.hasOwnProperty(i) && i != 'matrixIds') {
					wmtsParams[i] = options[i];
				}
			}
			this.wmtsParams = wmtsParams;
			this.matrixIds = options.matrixIds || this.getDefaultMatrix();
			L.setOptions(this, options);
		},

		onAdd: function(map) {
			L.TileLayer.prototype.onAdd.call(this, map);
		},

		getTileUrl: function(coords) {
			// (Point, Number) -> String
			const x = coords['x'];
			const y = coords['y'];
			const z = coords['z'];

			let url = L.TileLayer.prototype.getTileUrl.call(this, coords);

			let TileMatrix = `${this.options.tilematrixSet}:${this.matrixIds[z].identifier}`;

			return (
				url +
				getParamString(this.wmtsParams, url, this.options.uppercase) +
				'&TileMatrix=' +
				TileMatrix +
				'&TileRow=' +
				y +
				'&TileCol=' +
				x
			);
		},

		setParams: function(params, noRedraw) {
			L.extend(this.wmtsParams, params);
			if (!noRedraw) {
				this.redraw();
			}
			return this;
		},

		getDefaultMatrix: function() {
			/**
			 * the matrix3857 represents the projection
			 * for in the IGN WMTS for the google coordinates.
			 */
			let matrixIds3857 = new Array(22);
			for (let i = 0; i < 22; i++) {
				matrixIds3857[i] = {
					identifier: '' + i,
					topLeftCorner: new L.LatLng(20037508.3428, -20037508.3428),
				};
			}
			return matrixIds3857;
		},
	});
	HorusLeaflet.wmtsLayer = function(url, options) {
		return new HorusLeaflet.TileLayer.WMTS(url, options);
	};

	return HorusLeaflet;
}, window);
